// Core
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { motion, useAnimation } from 'framer-motion';
import { 
	Box, 
	Image, 
	Divider, 
	Button, 
	Grid, 
	Text, 
	Flex,
	Modal,
	ModalOverlay,
	ModalCloseButton,
	ModalContent,
	Skeleton,
	useColorModeValue,
	useDisclosure,
	useColorMode,
	useBreakpointValue
} from '@chakra-ui/react';
import { NavLink, Link } from 'react-router-dom';

// Assets
import { apiService } from 'services/ApiService';
import { validateLoadMore, getDiscount } from 'services/Utils';
import placeholder from 'assets/img/placeholder-partner.jpg';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import cardPartner from 'assets/img/card-partner.png';

// Components
import Pagination from 'components/Pagination';
import Card from 'components/Card/Card';
import PartnerModal from './partners/PartnerModal';
import AffiliateCashbackModal from './affiliate-cashback/AffiliateCashbackModal';
import AffiliatePartnerModal from './affiliate-partners/AffiliatePartnerModal';

const Partners = ({
	userData,
	type, 
	sort, 
	tops, 
	limit, 
	search, 
	categoryId, 
	tagIds, 
	locations,
	isPopular,
	isFeatured,
	isShop,
	isGrid,
	includeLoadMore, 
	isStores,
	setFilterResults,
	nearMe,
	userCoords,
	discountsRange,
	isReady,
	isLoadingPage,
	setIsLoadingPage,
	partnersIsHidden,
	setPartnersIsHidden
}) => {
	const textColor = useColorModeValue('black', '#9FABCB');
	const bgColorSeeAll = useColorModeValue('white', '#0e1633');

	const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

	const { isOpen, onClose, onOpen } = useDisclosure();

	const [offset, setOffset] = useState(0);

	const [data, setData] = useState([]);
	const [dataMeta, setDataMeta] = useState({total: 0, offset, limit}); 

    const { isLoading } = useQuery({
        queryKey: [type, nearMe, discountsRange, sort, search, offset, tops, categoryId, tagIds, locations, isFeatured, isPopular, isShop],
        queryFn: async () => {
        	if (nearMe && userCoords.lat === 0 && userCoords.long === 0) {
		      return null; 
		    }

        	let params = { offset };
        	//if(type) { params = {...params, type}; }
        	if(sort) { params = {...params, sort}; }
        	if(tops) { params = {...params, tops}; }
        	if(limit) { params = {...params, limit}; }
        	if(search) { params = {...params, search}; }
        	if(categoryId) { params = {...params, categoryIds: categoryId }; }
        	if(tagIds) { params = {...params, tagIds }; }
        	if(locations) { params = {...params, locations }; }
        	if(isPopular) { params = {...params, sort: isPopular }; }
        	if(isFeatured) { params = {...params, tops: isFeatured }; }
        	if(isShop) { params = {...params, tops: 'shop' }; }
        	if(nearMe) { params = {...params, lat: userCoords.lat, long: userCoords.long}; }
        	if(discountsRange) { params = {...params, discount: discountsRange }; }

        	let response = { data: [], meta: {}};

        	if(type === 'partner' ){
        		response = await apiService.getPartners(params);
        	} else if(type === 'affiliate-partner'){
        		params = {...params, tops: 'affiliate' };
        		response = await apiService.getPartners(params);
        	} else { // affiliate-cashback
				response = await apiService.getAffiliateСashback(params);   
        	}

        	setData(response.data);
        	
			if(setFilterResults){
				setFilterResults(response.meta.total);
			}

			setDataMeta(response.meta);
        	
        	if(response.data.length > 0){
                setPartnersIsHidden(false);
            }

            setIsLoadingPage(false);

        	return true;
        },
        refetchOnWindowFocus: false,
        enabled: isReady,
        retry: false
    });

	const [activeItem, setActiveItem] = useState({});
	const showItem = async (item) => {
		setActiveItem(item);
		onOpen();

		if(userData){
			try { await apiService.clickPartner({id: item?.id}); } catch(e) {}
		}
	};

    return (
    	<>
    		{ !isStores ? (
		        <Grid 
		        	templateColumns={ 
	        			(type === 'partner' && includeLoadMore) ? 
	        				{ sm: '1fr', md: '1fr 1fr', lg: '1fr 1fr' }
	        			:
	        				{ sm: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }	
	        		} 
		        	gap="24px" 
		        	templateRows='1fr'>
		        	{ data.length > 0 ? data.map((item, index) => (
				        <Card key={index} overflow="hidden" cursor="pointer" padding="0" position="relative" 
				        	onClick={() => showItem(item)} opacity={ isLoadingPage ? '0.3' : '1' }>
				        	<Image src={item?.image ?? placeholder} alt="partner" objectFit="cover" maxH="147px" h="100%" />
				        	<Box p="12px 12px 16px 12px">
				        		<Text variant="header" fontSize="16px" textTransform="normal" mb="12px" letterSpacing="0px">
				        			{ type === 'partner' || type === 'affiliate-partner'  ? item.name : item.title }
				        		</Text>

				        		{ type === 'partner' ? (
				        			<>
										<Text variant="title-lg" fontSize="22px" fontWeight="700" mb="2px" mt="24px">{getDiscount(item)}</Text>
				        				<Text variant="text-sm" fontWeight="500" color={textColor}>{item?.categories?.map(item => item.name).join(', ') || '-'}</Text>
				        			</>
				        		) : (
				        			<Text 
									  variant="text-sm" 
									  fontWeight="500"
									  color={textColor}
									  sx={{
									    display: '-webkit-box',
									    WebkitBoxOrient: 'vertical',
									    WebkitLineClamp: 2, 
									    overflow: 'hidden'
									  }}
									>
									  {item?.description}
									</Text>
				        		)}
				        	</Box>
				        	
				        	<Lines className="abstract-lines" style={{ zIndex: '1', top: '-35%', transform: 'rotate(259deg) scale(0.4)', right: '-80%', left: 'unset' }} />
				        </Card>
				    )) : isLoading && data.length === 0 ? (
				    	<>
				    		{(type === 'partner' && includeLoadMore) ? 
	        				(<>
	        					<Skeleton height="200px" borderRadius="16px" mb="24px" />
	        					<Skeleton height="200px" borderRadius="16px" mb="24px" />
	        				</>)
	        			:
	        				(<>
					    		<Skeleton height="200px" borderRadius="16px" mb="24px" />
					    		<Skeleton height="200px" borderRadius="16px" mb="24px" />
					    		<Skeleton height="200px" borderRadius="16px" mb="24px" />
				    		</>)}
				    	</>
				    ) : (<Text p="24px" display={ partnersIsHidden ? 'none' : 'block' }>Partners not found</Text>)}

				    { sort === 'mostPop' && (
				    	<Card overflow="hidden" padding="0" position="relative" className="animation-partners-popular">
				        	<Image className="animation-partners-popular-scale" src={cardPartner} alt="partner all" objectFit="cover" maxH="70px" h="100%" />
				        	<Box p="12px" zIndex="3" bg={bgColorSeeAll}>
				        		<Text variant="header" mt="20px" fontSize="16px" textTransform="normal" mb="12px" letterSpacing="0px">
				        			Explore Top Partners
				        		</Text>
			        			<Text 
								  variant="text-sm" 
								  fontWeight="500"
								  color={textColor}
								  mb="12px"
								  sx={{
								    display: '-webkit-box',
								    WebkitBoxOrient: 'vertical',
								    WebkitLineClamp: 2, 
								    overflow: 'hidden'
								  }}
								>
								  Discover all our most popular partners in one place
								</Text>

								<Button as={NavLink} mt="16px" to="/discounts/partners?sort=mostPop" variant={ isDark ? 'button-lg-g' : 'button-lg' }>See All</Button>
				        	</Box>
				        	
				        	<Lines className="abstract-lines" style={{ zIndex: '1', top: '-35%', transform: 'rotate(259deg) scale(0.4)', right: '-80%', left: 'unset' }} />
				        </Card>
				    )}
		        </Grid>
	        ) : (
	        	<>
		        	{ isGrid ? (
			        	<Grid 
				        	templateColumns={ 
			        			(type === 'partner' && includeLoadMore) ? 
			        				{ sm: '1fr', md: '1fr 1fr', lg: '1fr 1fr' }
			        			:
			        				{ sm: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }	
			        		} 
				        	gap="24px" 
				        	templateRows='1fr'>

				        	{ data.length > 0 ? data.map((item, index) => (
				        		<Card key={index} overflow="hidden" cursor="pointer" padding="8px 12px" position="relative" 
				        			onClick={() => showItem(item)} >
					        		<Flex gap="12px" alignItems="center">
					        			<Image src={item?.image ?? placeholder} 
					        				alt="partner" 
					        				objectFit="cover"
					        				borderRadius="12px" 
					        				maxW="118px" w="100%"
					        				maxH="81px" h="100%" />
					        			<Text variant="title-md" fontWeight="900" fontStyle="italic">{item?.name}</Text>
					        		</Flex>
					        		<Lines className="abstract-lines" style={{ zIndex: '1', top: '-200%', transform: 'rotate(259deg) scale(0.7)', left: '0' }} />
					        	</Card>
				        	)) : (
				        		<>
				        			<Flex gap="12px">
				        				<Skeleton maxW="118px" w="100%" height="81px" borderRadius="16px" />
				        				<Skeleton w="100%" height="81px" borderRadius="16px" />
				        			</Flex>

				        			<Flex gap="12px">
				        				<Skeleton maxW="118px" w="100%" height="81px" borderRadius="16px" />
				        				<Skeleton w="100%" height="81px" borderRadius="16px" />
				        			</Flex>

				        			<Flex gap="12px">
				        				<Skeleton maxW="118px" w="100%" height="81px" borderRadius="16px" />
				        				<Skeleton w="100%" height="81px" borderRadius="16px" />
				        			</Flex>
				        		</>
				        	)}

				        </Grid>
			        ) : (
			        	<>
			        		{ data.length > 0 && data.map((item, index) => (
				        		<Flex cursor="pointer" key={index} gap="12px" alignItems="center" mb="16px" onClick={() => showItem(item)}>
				        			<Image src={item?.image ?? placeholder} 
				        				alt="partner" 
				        				objectFit="cover"
				        				borderRadius="12px" 
				        				maxW="90px" w="100%"
				        				maxH="62px" h="100%" />
				        			<Text variant="text-sm" fontWeight="500">{item?.name}</Text>
				        		</Flex>
				        	))}
			        	</>
			        )}
	        	</>
	        )}

	        { includeLoadMore && data.length !== 0 && (
		        <Pagination
		            meta={dataMeta}
		            isLoadingPage={isLoadingPage}
		            setIsLoadingPage={setIsLoadingPage}
		            currentPage={Math.floor(offset / limit) + 1}
		            onPageChange={(newOffset) => setOffset(newOffset)}
		          />
		    )}

		    { type === 'partner' ? (
		    	<PartnerModal item={activeItem} userData={userData} isOpen={isOpen} onOpen={onOpen} onClose={onClose}  />
		    ) : type === 'affiliate-cashback' ? (
		    	<AffiliateCashbackModal item={activeItem} userData={userData} isOpen={isOpen} onOpen={onOpen} onClose={onClose}  />
		    ) : ( // affiliate-partner
		    	<AffiliatePartnerModal item={activeItem} userData={userData} isOpen={isOpen} onOpen={onOpen} onClose={onClose}  />
			)}
	    </>
    );
};


export default Partners;